import React, { useState, useCallback, useMemo } from "react";
import FormField from "../../FormField/FormField";
import CustomInputSelect from "../../Common/CustomInputSelect/CustomInputSelect";
import CustomTooltip from "../../CustomTooltip/CustomTooltip";
import { MapIcon, FloorIcon, RackIcon } from "../../../assets/icons";
import CustomWarningModal from "../../CustomWarningModal";
import CustomSelect from "../../Common/CustomSelect/CustomSelect";
import "./Posizione.scss";

const Posizione = ({
  formData,
  depositiData = [],
  disabled,
  cassettaStructure,
  handleFormDataChange,
  handleMapView,
  handleResourceView,
}) => {
  const [pianoKey, setPianoKey] = useState(0);
  const [scaffaleKey, setScaffaleKey] = useState(0);
  const [ripianoKey, setRipianoKey] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const getDepositoByName = useCallback(
    (name) => depositiData.find((deposito) => deposito.nome === name),
    [depositiData]
  );
  const getPianoByName = useCallback(
    (deposito, name) => deposito.piani.find((piano) => piano.nome === name),
    []
  );
  const getScaffaleByName = useCallback(
    (piano, name) => piano.scaffali.find((scaffale) => scaffale.nome === name),
    []
  );

  const depositiOptions = useMemo(
    () =>
      depositiData.map((deposito) => ({
        value: deposito.nome,
        label: deposito.nome,
      })),
    [depositiData]
  );

  const pianoOptions = useMemo(() => {
    const selectedDeposito = getDepositoByName(formData["deposito"]);
    return selectedDeposito
      ? selectedDeposito.piani.map((piano) => ({
          value: piano.nome,
          label: piano.nome,
        }))
      : [];
  }, [formData, getDepositoByName]);

  const scaffaleOptions = useMemo(() => {
    const selectedDeposito = getDepositoByName(formData["deposito"]);
    const selectedPiano = selectedDeposito
      ? getPianoByName(selectedDeposito, formData["piano"])
      : null;
    return selectedPiano
      ? selectedPiano.scaffali.map((scaffale) => ({
          value: scaffale.nome,
          label: scaffale.nome,
        }))
      : [];
  }, [formData, getDepositoByName, getPianoByName]);

  const ripianoOptions = useMemo(() => {
    const selectedDeposito = getDepositoByName(formData["deposito"]);
    const selectedPiano = selectedDeposito
    ? getPianoByName(selectedDeposito, formData["piano"])
    : null;
    const selectedScaffale = selectedPiano
      ? getScaffaleByName(selectedPiano, formData["scaffale"])
      : null;
    return selectedScaffale
      ? selectedScaffale.ripiani.map((ripiano) => ({
          value: ripiano.nome,
          label: ripiano.nome,
        }))
      : [];
  }, [formData, getDepositoByName, getScaffaleByName]);

  const handleSelectChange = useCallback(
    (name, option) => {
      switch(name) {
        case "deposito":
          handleFormDataChange("deposito", option.value);
          handleFormDataChange("piano", "");
          handleFormDataChange("scaffale", "");
          handleFormDataChange("ripiano", "");
          setPianoKey((prevKey) => prevKey + 1);
          setScaffaleKey((prevKey) => prevKey + 1);
          setRipianoKey((prevKey) => prevKey + 1);
          break;
        case "piano":
          handleFormDataChange("piano", option.value);
          handleFormDataChange("scaffale", "");
          handleFormDataChange("ripiano", "");
          setScaffaleKey((prevKey) => prevKey + 1);
          setRipianoKey((prevKey) => prevKey + 1);
          break;
        case "scaffale":
          handleFormDataChange("scaffale", option.value);
          handleFormDataChange("ripiano", "");  
          setRipianoKey((prevKey) => prevKey + 1);
          break;
        case "ripiano":
          handleFormDataChange("ripiano", option.value);
          break;
        default:
          break;
      }
    },
    [handleFormDataChange, setPianoKey, setScaffaleKey, setRipianoKey]
  );

  const handleResource = useCallback(
    (resource) => {
      if (!formData[resource]) return;
      const selectedDeposito = getDepositoByName(formData["deposito"]);
      if (!selectedDeposito) return;
      if (resource === "piano") {
        const selectedPiano = getPianoByName(
          selectedDeposito,
          formData["piano"]
        );
        if (selectedPiano) handleResourceView(selectedPiano.allegato);
      } else if (resource === "scaffale") {
        const selectedPiano = getPianoByName(
          selectedDeposito,
          formData["piano"]
        );
        if (!selectedPiano) return;
        const selectedScaffale = selectedPiano.scaffali.find(
          (scaffale) => scaffale.nome === formData["scaffale"]
        );
        if (selectedScaffale) handleResourceView(selectedScaffale.allegato);
      }
    },
    [handleResourceView, formData, getDepositoByName, getPianoByName]
  );


  return (
    <div>
      <p className="section-title">POSIZIONE</p>
      <div className="posizione-wrapper">
        <CustomSelect
          options={depositiOptions}
          initValue={{
            label: formData["deposito"],
            value: formData["deposito"],
          }}
          name="deposito"
          label={"Deposito"}
          disabled={disabled}
          placeholder="Seleziona deposito"
          onChange={handleSelectChange}
        />
        {disabled && (
          <div className="posizione-row-icon">
            <CustomTooltip text="Visualizza su mappa">
              <button
                className="provenienze-icons"
                onClick={() => setShowModal(true)}
              >
                <MapIcon fillColor="var(--archeo-color)" />
              </button>
            </CustomTooltip>
          </div>
        )}
      </div>

      <div className="posizione-wrapper">
        <CustomSelect
          key={`piano-${pianoKey}`}
          options={pianoOptions.sort((a, b) => a.label.localeCompare(b.label))}
          initValue={{ label: formData["piano"], value: formData["piano"] }}
          name="piano"
          label={"Piano"}
          disabled={disabled}
          placeholder="Seleziona piano"
          onChange={handleSelectChange}
        />
        {disabled && formData["piano"] && (
          <div className="posizione-row-icon">
            <CustomTooltip text="Visualizza pianta">
              <button
                className="provenienze-icons"
                onClick={() => handleResource("piano")}
              >
                <FloorIcon fillColor="var(--archeo-color)" />
              </button>
            </CustomTooltip>
          </div>
        )}
      </div>

      <div className="posizione-wrapper">
        <CustomInputSelect  
          isCreatable={true}  
          key={`scaffale-${scaffaleKey}`}
          options={scaffaleOptions.sort((a, b) => a.label.localeCompare(b.label))}
          initValue={{
            label: formData["scaffale"],
            value: formData["scaffale"],
          }}
          name="scaffale"
          label={"Scaffale"}
          disabled={disabled}
          placeholder="Seleziona scaffale"
          onChange={handleSelectChange}
        />
        {false && formData["scaffale"] && (
          <div className="posizione-row-icon">
            <CustomTooltip text="Visualizza disposizione">
              <button
                className="provenienze-icons"
                onClick={() => handleResource("scaffale")}
              >
                <RackIcon fillColor="var(--archeo-color)" />
              </button>
            </CustomTooltip>
          </div>
        )}
      </div>

      <div className="posizione-wrapper">
        <CustomInputSelect
          isCreatable={true}
          key={`ripiano-${ripianoKey}`}
          options={ripianoOptions.sort((a, b) => a.label.localeCompare(b.label))}  
          initValue={{
            label: formData["ripiano"],
            value: formData["ripiano"],
          }}
          name="ripiano"
          label={"Ripiano"}
          disabled={disabled}
          placeholder="Seleziona ripiano"
          onChange={handleSelectChange}
        />
      </div>


      <CustomWarningModal
        isOpen={showModal}
        text={"Risorsa attualmente non disponibile."}
        onClose={() => setShowModal(false)}
      />
    </div>
  );
};

export default React.memo(Posizione);
